import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useEntities } from '@/hooks/use-entities';
import { useSubscription } from '@/hooks/useSubscription';
import type { Storyboard, StoryboardPanel } from '@/lib/storyboard-types';
import type { Character, Location, Scene } from '@/lib/types';
import { useCallback, useEffect, useState } from 'react';

// Generate unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);
type UnknownRecord = Record<string, unknown>;

// Recursive helper to remove undefined values from objects and arrays
const deepStripUndefined = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(deepStripUndefined);
  }
  if (obj !== null && typeof obj === 'object') {
    const rec = obj as UnknownRecord;
    return Object.keys(rec).reduce<UnknownRecord>((acc, key) => {
      const value = rec[key];
      if (value !== undefined) {
        acc[key] = deepStripUndefined(value);
      }
      return acc;
    }, {});
  }
  return obj;
};

export function useStoryboard(novelId: string | null) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { isTokenDepleted } = useSubscription();
  const [selectedScene, setSelectedScene] = useState<Scene | null>(null);
  const [storyboard, setStoryboard] = useState<Storyboard | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingStoryboard, setIsLoadingStoryboard] = useState(false);

  // Use the decoupled hooks to get characters and locations
  const { data: characters } = useEntities<Character>('characters');
  const { data: locations } = useEntities<Location>('locations');

  // Fetch storyboard for selected scene
  useEffect(() => {
    if (!novel.id || !selectedScene || !backend) {
      setStoryboard(null);
      setIsLoadingStoryboard(false);
      return;
    }

    setIsLoadingStoryboard(true);

    const unsubscribe = backend.narrative.subscribeStoryboardForScene<Storyboard>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      selectedScene.id,
      (storyboards) => {
        if (storyboards && storyboards.length > 0) {
          setStoryboard(storyboards[0]);
        } else {
          setStoryboard(null);
        }
        setIsLoadingStoryboard(false);
      },
      (error) => {
        console.error('Failed to load storyboard:', error);
        setIsLoadingStoryboard(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel, selectedScene]);

  // Generate storyboard script from scene
  const generateScript = useCallback(async () => {
    if (!selectedScene || !novel.id || !backend) return;

    setIsGenerating(true);

    try {
      // Get related entities
      const sceneCharacters = characters?.filter(
        (c) => selectedScene.characterIds?.includes(c.id)
      ) || [];
      const sceneLocations = locations?.filter(
        (l) => selectedScene.locationIds?.includes(l.id)
      ) || [];

      // Call the AI Service
      const data = await backend.ai.generateStoryboardScript({
        novelId: novel.id,
        scene: {
          id: selectedScene.id,
          name: selectedScene.name,
          description: selectedScene.description,
          settings: selectedScene.settings,
          pointOfScene: selectedScene.pointOfScene,
          themes: selectedScene.themes,
        },
        characters: sceneCharacters.map((c) => ({
          id: c.id,
          name: c.name,
          type: c.type || '',
          description: c.description || '',
        })),
        locations: sceneLocations.map((l) => ({
          id: l.id,
          name: l.name,
          description: l.description || '',
        })),
      });

      // Create storyboard document
      const newStoryboard: Storyboard = {
        id: generateId(),
        sceneId: selectedScene.id,
        sceneName: selectedScene.name,
        sceneDescription: selectedScene.description || '',
        strips: data.strips,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'draft',
      };

      // Save using Narrative Store
      await backend.narrative.setNarrativeItem(
        novel.ownerId,
        novel,
        'storyboards',
        newStoryboard
      );

      setStoryboard(newStoryboard);
    } catch (error) {
      console.error('Failed to generate storyboard:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedScene, novel, backend, characters, locations]);

  // Update a panel
  const updatePanel = useCallback(
    async (stripId: string, panelId: string, updates: Partial<StoryboardPanel>) => {
      if (!storyboard || !novel.id || !backend) return;

      // Update local state
      setStoryboard((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          strips: prev.strips.map((strip) => {
            if (strip.id !== stripId) return strip;
            return {
              ...strip,
              panels: strip.panels.map((panel) => {
                if (panel.id !== panelId) return panel;
                return { ...panel, ...updates };
              }),
            };
          }),
          updatedAt: new Date().toISOString(),
        };
      });

      // Update in Backend Narrative Store
      try {
        const updatedStrips = deepStripUndefined(
          storyboard.strips.map((strip) => {
            if (strip.id !== stripId) return strip;
            return {
              ...strip,
              panels: strip.panels.map((panel) => {
                if (panel.id !== panelId) return panel;
                return { ...panel, ...updates };
              }),
            };
          })
        );

        await backend.narrative.updateNarrativeItem(
          novel.ownerId,
          novel,
          'storyboards',
          storyboard.id,
          {
            strips: updatedStrips,
            updatedAt: new Date().toISOString(),
          }
        );
      } catch (error) {
        console.error('Failed to update panel:', error);
      }
    },
    [storyboard, novel, backend]
  );

  // Batch update multiple panels in a single strip
  const batchUpdatePanels = useCallback(
    async (stripId: string, updates: { panelId: string; updates: Partial<StoryboardPanel> }[]) => {
      if (!storyboard || !novel.id || !backend) return;

      // Update local state
      setStoryboard((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          strips: prev.strips.map((strip) => {
            if (strip.id !== stripId) return strip;
            return {
              ...strip,
              panels: strip.panels.map((panel) => {
                const panelUpdate = updates.find((u) => u.panelId === panel.id);
                if (!panelUpdate) return panel;
                return { ...panel, ...panelUpdate.updates };
              }),
            };
          }),
          updatedAt: new Date().toISOString(),
        };
      });

      // Update Backend Narrative Store
      try {
        const updatedStrips = deepStripUndefined(
          storyboard.strips.map((strip) => {
            if (strip.id !== stripId) return strip;
            return {
              ...strip,
              panels: strip.panels.map((panel) => {
                const panelUpdate = updates.find((u) => u.panelId === panel.id);
                if (!panelUpdate) return panel;
                return { ...panel, ...panelUpdate.updates };
              }),
            };
          })
        );

        await backend.narrative.updateNarrativeItem(
          novel.ownerId,
          novel,
          'storyboards',
          storyboard.id,
          {
            strips: updatedStrips,
            updatedAt: new Date().toISOString(),
          }
        );
      } catch (error) {
        console.error('Failed to batch update panels:', error);
      }
    },
    [storyboard, novel, backend]
  );

  // Regenerate a panel
  const regeneratePanel = useCallback(
    async (stripId: string, panelId: string, changeRequest?: string) => {
      if (!storyboard || !selectedScene || !novel.id || !backend) return;

      // Get the current panel
      const strip = storyboard.strips.find((s) => s.id === stripId);
      const panel = strip?.panels.find((p) => p.id === panelId);
      if (!panel) return;

      // Mark as generating
      updatePanel(stripId, panelId, { scriptStatus: 'pending' });

      try {
        const data = await backend.ai.regeneratePanelScript({
          panel,
          changeRequest,
          sceneContext: {
            name: selectedScene.name,
            description: selectedScene.description,
          },
        });

        updatePanel(stripId, panelId, {
          ...data.panel,
          scriptStatus: 'pending',
          changeRequest: undefined,
        });
      } catch (error) {
        console.error('Failed to regenerate panel:', error);
      }
    },
    [storyboard, selectedScene, novel, backend, updatePanel]
  );

  // Track which panels are currently generating images
  const [generatingImagePanels, setGeneratingImagePanels] = useState<Set<string>>(new Set());

  // Generate image for a panel
  const generatePanelImage = useCallback(
    async (stripId: string, panelId: string) => {
      if (!storyboard || !novel.id || !backend) return;

      const strip = storyboard.strips.find((s) => s.id === stripId);
      const panel = strip?.panels.find((p) => p.id === panelId);
      if (!panel) return;

      setGeneratingImagePanels((prev) => new Set(prev).add(panelId));
      updatePanel(stripId, panelId, { imageStatus: 'generating' });

      try {
        const data = await backend.ai.generatePanelImage({
          novelId: novel.id,
          storyboardId: storyboard.id,
          panelId,
          visualDescription: panel.visualDescription,
          camera: panel.camera,
          composition: panel.composition,
          characters: panel.characters,
        });

        const newIteration = {
          id: data.iterationId,
          imageUrl: data.imageUrl,
          storagePath: data.storagePath,
          prompt: panel.visualDescription,
          createdAt: new Date().toISOString(),
        };

        updatePanel(stripId, panelId, {
          imageStatus: 'reviewing',
          imageIterations: [...(panel.imageIterations || []), newIteration],
        });
      } catch (error) {
        console.error('Failed to generate panel image:', error);
        updatePanel(stripId, panelId, { imageStatus: 'not_started' });
      } finally {
        setGeneratingImagePanels((prev) => {
          const next = new Set(prev);
          next.delete(panelId);
          return next;
        });
      }
    },
    [storyboard, novel, backend, updatePanel]
  );

  const refinePanelImage = useCallback(
    async (stripId: string, panelId: string, changeRequest: string) => {
      if (!storyboard || !novel.id || !backend) return;

      const strip = storyboard.strips.find((s) => s.id === stripId);
      const panel = strip?.panels.find((p) => p.id === panelId);
      const currentIterations = panel?.imageIterations || [];
      if (!panel || currentIterations.length === 0) return;

      setGeneratingImagePanels((prev) => new Set(prev).add(panelId));
      updatePanel(stripId, panelId, { imageStatus: 'generating' });

      try {
        const lastIteration = currentIterations[currentIterations.length - 1];

        const data = await backend.ai.refinePanelImage({
          novelId: novel.id,
          storyboardId: storyboard.id,
          panelId,
          previousImageUrl: lastIteration.imageUrl,
          changeRequest,
          originalVisualDescription: panel.visualDescription,
        });

        const newIteration = {
          id: data.iterationId,
          imageUrl: data.imageUrl,
          storagePath: data.storagePath,
          prompt: panel.visualDescription,
          changeRequest,
          createdAt: new Date().toISOString(),
        };

        updatePanel(stripId, panelId, {
          imageStatus: 'reviewing',
          imageIterations: [...currentIterations, newIteration],
        });
      } catch (error) {
        console.error('Failed to refine panel image:', error);
        updatePanel(stripId, panelId, { imageStatus: 'reviewing' });
      } finally {
        setGeneratingImagePanels((prev) => {
          const next = new Set(prev);
          next.delete(panelId);
          return next;
        });
      }
    },
    [storyboard, novel, backend, updatePanel]
  );

  const acceptPanelImage = useCallback(
    (stripId: string, panelId: string, iterationIndex: number) => {
      updatePanel(stripId, panelId, {
        imageStatus: 'accepted',
        finalImageIndex: iterationIndex,
      });
    },
    [updatePanel]
  );

  return {
    selectedScene,
    setSelectedScene: (scene: Scene | null) => setSelectedScene(scene),
    storyboard,
    isGenerating: isGenerating || isLoadingStoryboard,
    generateScript,
    updatePanel,
    batchUpdatePanels,
    regeneratePanel,
    generatePanelImage,
    refinePanelImage,
    acceptPanelImage,
    isGeneratingImage: (panelId: string) => generatingImagePanels.has(panelId),
    isTokenDepleted,
  };
}
